/**
 * AuthBackground
 *
 * Branded full-bleed background for the authentication screens (login /
 * logout), per Figma "Foltrigg AI / Blue Text Branding" Login frame
 * (node 16732:38085).
 *
 * Layers, back to front:
 *  1. Cream base (#F2F0ED) over a blue underlay (#347FA5).
 *  2. A skewed conic-gradient "light fold" overlay at mix-blend-multiply —
 *     the exact gradient Figma exports. It is rendered as an INLINE <svg> +
 *     <foreignObject> rather than a CSS background image on purpose: browsers
 *     refuse to render foreignObject HTML when an SVG is loaded as an image,
 *     but render it fine when the SVG lives in the DOM. preserveAspectRatio
 *     "none" lets it stretch to fill any viewport.
 *  3. Two faint triangular textures (40% opacity, mix-blend-screen) that frame
 *     the auth card, mirroring the two Figma "Triangle Pattern" instances.
 *
 * Decorative only: aria-hidden and pointer-events-none so it never traps focus
 * or interferes with the auth card above it.
 */

// Exact conic gradient emitted by Figma for the Frame 16 "fold" overlay.
const FOLD_CONIC_GRADIENT =
  'conic-gradient(from 90deg, rgb(245, 244, 241) -45.365%, rgb(245, 244, 241) 7.1675%, rgb(194, 202, 206) 11.751%, rgb(143, 161, 171) 16.335%, rgb(91, 119, 136) 20.918%, rgb(66, 98, 119) 23.21%, rgb(40, 77, 101) 25.502%, rgb(66, 117, 150) 40%, rgb(111, 149, 173) 40.125%, rgb(156, 181, 196) 40.25%, rgb(200, 212, 218) 40.375%, rgb(245, 244, 241) 40.5%, rgb(245, 244, 241) 54.635%, rgb(245, 244, 241) 107.17%)';

export function AuthBackground() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden bg-[#347FA5]"
    >
      {/* Cream base */}
      <div className="absolute inset-0 bg-[#F2F0ED]" />

      {/* Skewed conic-gradient "light fold" (mix-blend-multiply over the base).
          Figma's gradient frame is 2901×1769 positioned at left:0 / top:-373
          inside the 1440×1024 screen, so the screen shows only the top-left
          crop of it (the conic center — the bright fold — lands at the right
          edge, ~mid height). We mirror that placement as viewport-relative
          percentages so the crop, not the whole gradient, fills the screen:
            left   = 0
            top    = -373 / 1024  = -36.43%
            width  = 2901 / 1440  =  201.46%
            height = 1769 / 1024  =  172.75% */}
      <svg
        className="absolute mix-blend-multiply"
        style={{ left: 0, top: '-36.43%', width: '201.46%', height: '172.75%' }}
        viewBox="0 0 2901 1769"
        preserveAspectRatio="none"
        aria-hidden
      >
        <g transform="matrix(66.651 68.291 -117.37 60.319 1450.5 884.5)">
          <foreignObject x="-302.01" y="-302.01" width="604.01" height="604.01">
            {/* React renders this div in the HTML namespace inside foreignObject. */}
            <div
              style={{
                width: '100%',
                height: '100%',
                backgroundImage: FOLD_CONIC_GRADIENT,
              }}
            />
          </foreignObject>
        </g>
      </svg>

      {/* Triangle textures — decorative, screen-blended so they shimmer on the
          blue and fade on the cream. Positions mirror the two Figma instances. */}
      {/* eslint-disable-next-line @next/next/no-img-element -- static decorative SVG, no optimization needed */}
      <img
        src="/auth-triangle-pattern.svg"
        alt=""
        aria-hidden
        className="absolute h-[643px] w-[648px] -translate-x-1/2 -translate-y-1/2 opacity-40 mix-blend-screen"
        style={{ left: 'calc(50% - 18px)', top: 'calc(50% + 183.5px)' }}
      />
      {/* eslint-disable-next-line @next/next/no-img-element -- static decorative SVG, no optimization needed */}
      <img
        src="/auth-triangle-pattern.svg"
        alt=""
        aria-hidden
        className="absolute h-[643px] w-[648px] -translate-x-1/2 -translate-y-1/2 opacity-40 mix-blend-screen"
        style={{ left: 'calc(50% + 126px)', top: 'calc(50% - 56.5px)' }}
      />
    </div>
  );
}
